import React from "react";
import { CustomIcon } from "../shared/CustomIcon";
import { computeStatusRing } from "../shared/Avatar";

// =============================================================
// NodeCircle — one member circle in the paginated visual tree.
// Renders the profile photo (or initials), status dot, and the
// abbreviated Greek level label + 7-digit number below.
// =============================================================
// Iter-73 — abbreviations for human levels 1..23.
// Level 0 is Alpha (God) which is rendered via the isGodNode branch above,
// so this array only needs to cover Beta..Omega. Indexed by (level - 1).
const LEVEL_ABBR = [
  "Be", "Ga", "De", "Ep", "Ze", "Et", "Th", "Io",
  "Ka", "La", "Mu", "Nu", "Xi", "Om", "Pi", "Rh",
  "Si", "Ta", "Up", "Ph", "Ch", "Ps", "Og",
];

export function nodeHealth(node, healthOverride) {
  if (healthOverride) return healthOverride;
  if (!node) return "vacant";
  if (node.status === "Deceased") return "deceased";
  if (node.status === "Transferred" || node.status === "Inactive" || node.status === "Removed") return "inactive";
  if (!node.memberId) return "vacant";
  return "active";
}

const HEALTH_DOT = {
  active:   "#4ade80",
  amber:    "#f59e0b",
  red:      "#ff6b6b",
  inactive: "rgba(200,200,200,0.55)",
  deceased: "#a5b4fc",
  vacant:   "var(--kc-accent)",
};
const HEALTH_BORDER = {
  active:   "#4ade80",
  amber:    "#f59e0b",
  red:      "#ff6b6b",
  inactive: "rgba(200,200,200,0.35)",
  deceased: "rgba(160,160,220,0.55)",
  vacant:   "color-mix(in srgb, var(--kc-accent) 55%, transparent)",
};

export default function NodeCircle({
  node, members, size = 60, highlighted = false, onClick, healthOverride,
}) {
  // Special rendering for the God node: crown circle with gold glow.
  if (node?.isGodNode) {
    const S = size;
    return (
      <button
        type="button"
        data-testid="ptn-god-node"
        data-node-id={node.nodeId}
        onClick={onClick}
        aria-label="God"
        style={{
          appearance: "none", background: "none", border: "none", cursor: "pointer", padding: 0,
          display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
          color: "inherit", font: "inherit",
        }}
      >
        <div style={{
          position: "relative", width: S, height: S, borderRadius: "50%",
          background: "radial-gradient(circle at 30% 30%, #F5D77A, #B8860B 65%, #6b4a05)",
          border: "2px solid #D4AF37",
          boxShadow: highlighted
            ? "0 0 0 3px rgba(212,175,55,0.75), 0 0 32px rgba(212,175,55,0.85)"
            : "0 0 20px rgba(212,175,55,0.6)",
          display: "flex", alignItems: "center", justifyContent: "center",
          animation: "godGlowPulse 3.6s ease-in-out infinite",
        }}>
          <CustomIcon name="crown" size={Math.round(S * 0.55)} />
          <style>{`
            @keyframes godGlowPulse {
              0%,100% { box-shadow: 0 0 20px rgba(212,175,55,0.55); }
              50%    { box-shadow: 0 0 28px rgba(212,175,55,0.85); }
            }
          `}</style>
        </div>
        <div style={{ textAlign: "center", lineHeight: 1.15 }}>
          <div style={{
            fontFamily: "'Cinzel',serif", fontSize: size >= 70 ? 12 : 10,
            color: "#D4AF37", fontWeight: 700, letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}>Alpha</div>
        </div>
      </button>
    );
  }
  const health = nodeHealth(node, healthOverride);
  const member = node?.memberId ? (members || []).find(m => m.id === node.memberId) : null;
  // Human levels are 1-based (Beta=1). LEVEL_ABBR[0] = "Be" corresponds to level 1.
  const abbr = (node?.level && node.level >= 1) ? (LEVEL_ABBR[node.level - 1] || "?") : "?";
  const num = node?.memberNumber || null;
  const borderColor = HEALTH_BORDER[health];
  const dotColor = HEALTH_DOT[health];
  const isVacant = health === "vacant";
  const initials = member
    ? ((member.firstName?.[0] || "") + (member.lastName?.[0] || "")) || "?"
    : "";

  return (
    <button
      type="button"
      data-testid={`ptn-${node?.slotRef || "unknown"}`}
      data-node-id={node?.nodeId}
      onClick={onClick}
      style={{
        appearance: "none", background: "none", border: "none", cursor: "pointer", padding: 0,
        display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
        color: "inherit", font: "inherit",
      }}
    >
      <div style={{ position: "relative", width: size, height: size }}>
        {(() => {
          const ring = member ? computeStatusRing(member.memberStatus) : null;
          const innerBorder = ring ? ring.border : (isVacant ? `2px dashed ${borderColor}` : `2px solid ${borderColor}`);
          const innerShadow = ring ? ring.boxShadow : "none";
          const highlightShadow = highlighted ? "0 0 0 3px color-mix(in srgb, var(--kc-accent) 55%, transparent), 0 0 22px color-mix(in srgb, var(--kc-accent) 55%, transparent)" : "";
          return (
            <div style={{
              width: size, height: size, borderRadius: "50%",
              border: innerBorder,
              background: member?.photo ? `#111 center/cover no-repeat url(${JSON.stringify(member.photo)})` : (isVacant ? "transparent" : "#2a2a2a"),
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "var(--kc-accent)", fontFamily: "'Cinzel',serif", fontWeight: 700,
              fontSize: size >= 70 ? 22 : size >= 55 ? 18 : 13,
              boxShadow: [highlightShadow, innerShadow].filter(Boolean).join(", ") || "none",
              transition: "box-shadow 0.15s",
            }}>
              {isVacant ? (
                <CustomIcon name="plus" size={Math.round(size / 3)} />
              ) : member?.photo ? null : (
                <span>{initials}</span>
              )}
            </div>
          );
        })()}
        {/* status dot / deceased icon */}
        {health === "deceased" ? (
          <span style={{
            position: "absolute", right: -4, bottom: -4, width: 20, height: 20,
            borderRadius: "50%", background: "var(--kc-surface)", border: `2px solid ${HEALTH_BORDER.deceased}`,
            display: "flex", alignItems: "center", justifyContent: "center", color: HEALTH_DOT.deceased,
          }}>
            <CustomIcon name="dove" size={11} />
          </span>
        ) : (
          <span data-testid={`ptn-dot-${node?.slotRef || "unknown"}`} style={{
            position: "absolute", right: -2, bottom: -2, width: 14, height: 14,
            borderRadius: "50%", background: dotColor,
            border: "2px solid #1A1A1A",
          }} />
        )}
      </div>
      <div style={{ textAlign: "center", lineHeight: 1.15 }}>
        <div style={{
          fontFamily: "'Cinzel',serif", fontSize: size >= 70 ? 12 : 10,
          color: "var(--kc-accent)", fontWeight: 700, letterSpacing: "0.06em",
        }}>
          {abbr}{num ? ` · #${num}` : ""}
        </div>
        {member && size >= 55 && (
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: "var(--kc-sub)", maxWidth: size + 40, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {(member.firstName || "").slice(0, 12)}
          </div>
        )}
      </div>
    </button>
  );
}

export { LEVEL_ABBR };
